// Admission only. The server remains authoritative; no Premium claim is trusted.
import { resolveCommercialControl } from '../../accountsEnvironment';
import { GAME_CONTENT_VERSION, createProgressAttemptId, getStudentPlaySession } from './ProgressData';
import { element, button, header } from '../util/menuUI';

const starts = new WeakMap();
export function commercialPlayEnabled() {
  return resolveCommercialControl({
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
    REACT_APP_ACCOUNTS_V1_ENABLED: process.env.REACT_APP_ACCOUNTS_V1_ENABLED,
    REACT_APP_ACCOUNTS_COMMERCIAL_ENABLED: process.env.REACT_APP_ACCOUNTS_COMMERCIAL_ENABLED,
    REACT_APP_ACCOUNTS_API_URL: process.env.REACT_APP_ACCOUNTS_API_URL,
    REACT_APP_ACCOUNTS_FRONTEND_ORIGIN: process.env.REACT_APP_ACCOUNTS_FRONTEND_ORIGIN,
    REACT_APP_PAYPAL_C3C_ENABLED: process.env.REACT_APP_PAYPAL_C3C_ENABLED,
  });
}
function descriptor(scene, kind) {
  return {
    planet_id: scene.registry.get('currentPlanet') || 'suma',
    mission_index: scene.registry.get('currentMissionData')?.index ?? 0,
    mode: kind === 'practice' ? 'practice' : scene.registry.get('adaptivePractice') ? 'adaptive' : 'adventure',
  };
}
function message(error) {
  if (error.progress) return 'Primero debemos guardar tu progreso pendiente. Reintenta antes de comenzar.';
  switch (error.status) {
    case 401: return 'No pudimos confirmar tu sesión. Reintenta o vuelve al mapa.';
    case 403: return 'Esta misión no está disponible para tu perfil. Pide ayuda a un adulto.';
    case 409: return 'No pudimos confirmar este intento. Vuelve al mapa para iniciar otro.';
    case 429: return 'Hay partidas pendientes de guardar. Reintenta la sincronización antes de comenzar otra.';
    default: return 'No pudimos confirmar la partida. Comprueba la conexión y vuelve a intentar.';
  }
}

// Guests have no authenticated entitlement. Only canonical first missions are free.
const GUEST_FREE_PLANETS = new Set(['suma', 'resta', 'mult', 'division']);
function allowGuestFreeMission(scene, kind) {
  const mission = descriptor(scene, kind);
  return GUEST_FREE_PLANETS.has(mission.planet_id) &&
    mission.mission_index === 0 &&
    !scene.registry.get('adaptivePractice');
}
function showGuestPremiumGate(scene) {
  scene.awaitingPlayAuthorization = true;
  scene.physics.world.pause();
  const keyboard = scene.input.keyboard, wasEnabled = keyboard.enabled;
  keyboard.enabled = false;
  const root = element('section', 'tmag-menu');
  root.tabIndex = -1;
  header(root, 'Misión Premium');
  const notice = element('p', 'tmag-detail',
    'La primera misión de cada mundo es gratis. Para acceder a esta misión, pide a un adulto que active Premium en tu perfil.');
  notice.setAttribute('role', 'alert');
  const back = button('Volver al mapa', () => scene.scene.start('MissionScene'));
  root.append(notice, back);
  scene.game.canvas.parentElement.appendChild(root);
  root.focus({ preventScroll: true });
  const cleanup = () => {
    root.remove();
    keyboard.enabled = wasEnabled;
    scene.events.off('shutdown', cleanup);
    scene.events.off('destroy', cleanup);
  };
  scene.events.once('shutdown', cleanup);
  scene.events.once('destroy', cleanup);
}
export function startScenePlay(scene, kind, createRun) {
  const commercial = commercialPlayEnabled();
  const student = scene.registry.get('studentIdentity');
  if (!commercial || (!student && allowGuestFreeMission(scene, kind))) {
    scene.awaitingPlayAuthorization = false;
    createRun(); // Commercial OFF or canonical free guest mission: no extra requests.
    return;
  }
  if (!student) {
    showGuestPremiumGate(scene);
    return;
  }
  if (starts.has(scene)) return starts.get(scene).flight;
  const studentId = student.student_id;
  const expected = descriptor(scene, kind), signature = JSON.stringify(expected);
  let binding;
  try { binding = getStudentPlaySession(studentId); } catch { /* Safe error UI below. */ }
  const state = { id: createProgressAttemptId(), busy: false, cancelled: false, started: false };
  starts.set(scene, state);
  scene.awaitingPlayAuthorization = true;
  scene.physics.world.pause();
  const keyboard = scene.input.keyboard, wasEnabled = keyboard.enabled;
  keyboard.enabled = false;
  const root = element('section', 'tmag-menu');
  root.tabIndex = -1;
  header(root, 'Preparando tu misión…');
  const feedback = element('p', 'tmag-detail', 'Comprobando el acceso a esta partida…');
  feedback.setAttribute('role', 'status');
  const retry = button('Reintentar', () => attempt());
  const back = button('Volver al mapa', () => {
    if (state.cancelled || state.started) return;
    cancel();
    scene.scene.start('MissionScene');
  });
  root.append(feedback, retry, back);
  scene.game.canvas.parentElement.appendChild(root);
  root.focus({ preventScroll: true });
  const remove = () => { root.remove(); keyboard.enabled = wasEnabled; };
  function cancel() {
    state.cancelled = true;
    remove();
    if (starts.get(scene) === state) starts.delete(scene);
    window.removeEventListener('tmag:progress-status', changed);
    scene.events.off('shutdown', cancel);
    scene.events.off('destroy', cancel);
  }
  function changed() { if (binding && !binding.isCurrent()) cancel(); }
  scene.events.once('shutdown', cancel);
  scene.events.once('destroy', cancel);
  window.addEventListener('tmag:progress-status', changed);
  const current = () => !state.cancelled && starts.get(scene) === state && binding?.isCurrent() &&
    scene.registry.get('studentIdentity')?.student_id === studentId && JSON.stringify(descriptor(scene, kind)) === signature;
  async function attempt() {
    if (state.busy || state.cancelled || state.started) return state.flight;
    state.busy = true; retry.disabled = true;
    feedback.setAttribute('role', 'status'); feedback.textContent = 'Comprobando el acceso a esta partida…';
    state.flight = (async () => {
      try {
        if (!binding) throw Object.assign(new Error('Student session unavailable'), { status: 401 });
        try { await binding.flush(); } catch { throw Object.assign(new Error('Progress pending'), { progress: true }); }
        if (!current()) { cancel(); return; }
        const payload = { context: binding.context, client_session_id: state.id, game_content_version: GAME_CONTENT_VERSION, ...expected };
        const response = await binding.request('/student/play-authorizations', { method: 'POST', body: JSON.stringify(payload) });
        if (!current()) { cancel(); return; }
        if (response?.authorized !== true || response.client_session_id !== state.id) throw new Error('Invalid authorization');
        createRun({ id: state.id, isCurrent: binding.isCurrent });
        state.started = true;
        remove(); window.removeEventListener('tmag:progress-status', changed);
        scene.awaitingPlayAuthorization = false;
        scene.physics.world.resume();
      } catch (error) {
        if (state.cancelled) return;
        feedback.setAttribute('role', 'alert'); feedback.textContent = message(error);
      } finally { state.busy = false; retry.disabled = false; }
    })();
    return state.flight;
  }
  // Enter/Space on a focused native button dispatches one click; no second
  // Phaser shortcut is active while this screen owns the keyboard.
  attempt();
  return state.flight;
}
